import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { sessionAuthService } from '@/services/session-auth-service';
import { getFreshIdTokenClaims, reauthenticateOnce } from '@/lib/auth0-id-token';

export function useCurrentUser() {
  const { user, isAuthenticated, isLoading, getIdTokenClaims, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  // Initialize state from sessionStorage synchronously to avoid race condition
  // where SuperAdminRoute renders before useEffect loads the cached data
  const [adminData, setAdminData] = useState<any>(() => {
    const cached = sessionAuthService.getAdminInfo();
    // Validate cached data has required fields
    if (cached?.type === 'prosci_admin' && !cached?.level) {
      return null; // Stale data, will be refreshed
    }
    return cached;
  });
  const [isSessionLoaded, setIsSessionLoaded] = useState(() => {
    const cached = sessionAuthService.getAdminInfo();
    // Only consider loaded if valid data exists
    if (cached?.type === 'prosci_admin' && !cached?.level) {
      return false;
    }
    return sessionAuthService.isAuthenticated();
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function initializeSession() {
      // Don't try to fetch if Auth0 is still loading
      if (isLoading) {
        return;
      }

      // Check if we have an existing session in sessionStorage
      const hasSession = sessionAuthService.isAuthenticated();
      const adminInfo = sessionAuthService.getAdminInfo();

      // If we have a valid session with admin info, use it
      // This handles both license admin sessions AND cached prosci admin sessions
      // during Auth0 re-authentication on page refresh
      if (hasSession && adminInfo) {
        // Validate that prosci_admin sessions have required fields
        // If level is missing, clear stale data and re-authenticate
        if (adminInfo.type === 'prosci_admin' && !adminInfo.level) {
          sessionAuthService.clearSession();
        } else {
          setAdminData(adminInfo);
          setIsSessionLoaded(true);
          return;
        }
      }

      // Clear session only if not authenticated AND no existing session
      if (!isAuthenticated || !user?.sub) {
        if (!hasSession) {
          sessionAuthService.clearSession();
        }
        setIsSessionLoaded(false);
        return;
      }

      // Check if we already have a valid session
      if (sessionAuthService.isAuthenticated()) {
        const admin = sessionAuthService.getAdminInfo();
        setAdminData(admin);
        setIsSessionLoaded(true);
        return;
      }

      try {
        // Only handle Prosci admin authentication here
        // JWT authentication is handled by JWTAuthHandler
        if (user?.sub) {
          // The backend verifies the ID token. sid is optional: without it the
          // session just won't respond to OIDC backchannel logout.
          const claims = await getFreshIdTokenClaims({ getIdTokenClaims, getAccessTokenSilently })
            .catch((error) => {
              console.warn('[Auth] Could not get a fresh ID token:', error);
              return undefined;
            });
          if (!claims?.__raw && (await reauthenticateOnce(loginWithRedirect))) return;

          await sessionAuthService.authenticateProsciAdmin(user.sub, user.email, claims?.sid, claims?.__raw);
        }
        
        // Get the admin info after authentication
        const admin = sessionAuthService.getAdminInfo();
        setAdminData(admin);
        setIsSessionLoaded(true);
        setError(null);
      } catch (error) {
        console.error('Failed to authenticate:', error);
        setError(error instanceof Error ? error.message : 'Authentication failed');
        setIsSessionLoaded(false);
      }
    }

    initializeSession();
  }, [isAuthenticated, isLoading, user, getIdTokenClaims, getAccessTokenSilently, loginWithRedirect]);

  return { 
    data: adminData, 
    isLoading: isLoading || (!isSessionLoaded && isAuthenticated),
    isSessionLoaded, 
    error 
  };
}